import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import "./Sudoku.css";

export default function Sudoku() {
  const [grid, setGrid] = useState([]);
  const [solution, setSolution] = useState([]);
  const [message, setMessage] = useState("");

  // Generate a full valid Sudoku board using backtracking
  const generateBoard = () => {
    const board = Array.from({ length: 9 }, () => Array(9).fill(0));

    const isSafe = (board, row, col, num) => {
      for (let x = 0; x < 9; x++) {
        if (board[row][x] === num || board[x][col] === num) return false;
      }
      const startRow = row - row % 3;
      const startCol = col - col % 3;
      for (let i = 0; i < 3; i++)
        for (let j = 0; j < 3; j++)
          if (board[startRow + i][startCol + j] === num) return false;
      return true;
    };

    const fillBoard = () => {
      for (let i = 0; i < 9; i++) {
        for (let j = 0; j < 9; j++) {
          if (board[i][j] === 0) {
            const nums = [1,2,3,4,5,6,7,8,9].sort(() => Math.random() - 0.5);
            for (let num of nums) {
              if (isSafe(board, i, j, num)) {
                board[i][j] = num;
                if (fillBoard()) return true;
                board[i][j] = 0;
              }
            }
            return false;
          }
        }
      }
      return true;
    };

    fillBoard();
    return board;
  };

  const removeNumbers = (board, difficulty = 40) => {
    const puzzle = board.map(row => [...row]);
    let count = difficulty;
    while (count > 0) {
      const i = Math.floor(Math.random() * 9);
      const j = Math.floor(Math.random() * 9);
      if (puzzle[i][j] !== null) {
        puzzle[i][j] = null;
        count--;
      }
    }
    return puzzle;
  };

  const newPuzzle = (difficultyLevel = "Medium") => {
    const fullBoard = generateBoard();
    setSolution(fullBoard);
    const holes = difficultyLevel === "Easy" ? 35 : difficultyLevel === "Hard" ? 50 : 45;
    setGrid(removeNumbers(fullBoard, holes));
    setMessage("");
  };

  useEffect(() => {
    newPuzzle();
  }, []);

  const handleChange = (row, col, value) => {
    if (!/^[1-9]?$/.test(value)) return;
    const newGrid = [...grid];
    newGrid[row][col] = value === "" ? null : parseInt(value);
    setGrid(newGrid);
  };

  const checkSolution = () => {
    for (let i = 0; i < 9; i++)
      for (let j = 0; j < 9; j++)
        if (grid[i][j] !== solution[i][j]) {
          setMessage("❌ Incorrect! Keep trying!");
          return;
        }
    setMessage("🎉 Congratulations! You solved it!");
  };

  return (
    <div className="sudoku-wrapper">
      {/* SEO meta */}
      <Helmet>
        <title>Sudoku Online - Free Sudoku Puzzle Game</title>
        <meta name="description" content="Play Sudoku online for free. Responsive Sudoku with Easy, Medium, and Hard levels. Solve puzzles and challenge yourself!" />
        <meta name="keywords" content="sudoku, online sudoku, sudoku game, free sudoku, sudoku puzzle, responsive sudoku, sudoku online play" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <h2>Sudoku Game</h2>

      {/* Difficulty Selection */}
      <div className="difficulty-buttons">
        {["Easy", "Medium", "Hard"].map(level => (
          <button key={level} onClick={() => newPuzzle(level)}>{level}</button>
        ))}
      </div>

      {/* Rules */}
      <div className="sudoku-rules">
        <h3>Rules:</h3>
        <ul>
          <li>Fill the grid so each row, column, and 3x3 box contains numbers 1-9.</li>
          <li>Empty cells are clickable for input.</li>
          <li>Check your solution with the "Check" button.</li>
        </ul>
      </div>

      {/* Sudoku Grid */}
      <div className="sudoku-grid">
        {grid.map((row, rIndex) =>
          row.map((cell, cIndex) => (
            <input
              key={`${rIndex}-${cIndex}`}
              value={cell || ""}
              onChange={(e) => handleChange(rIndex, cIndex, e.target.value)}
              className={`cell 
                ${(rIndex % 3 === 2 && rIndex !== 8 ? "border-bottom" : "")} 
                ${(cIndex % 3 === 2 && cIndex !== 8 ? "border-right" : "")}`}
            />
          ))
        )}
      </div>

      <div className="sudoku-actions">
        <button onClick={checkSolution}>Check</button>
        <span className="message">{message}</span>
      </div>

      {/* AdSense Placeholder */}
      <div style={{ marginTop: "25px", textAlign: "center" }}>
        <div style={{
          width: "320px",
          height: "100px",
          backgroundColor: "#ccc",
          display: "inline-block",
          lineHeight: "100px",
          color: "#111"
        }}>
          Ad Space
        </div>
      </div>
    </div>
  );
}
